const resolveBaseUrl = () => {
  // 1. Create React App environment variable
  if (typeof process !== "undefined" && process.env && process.env.REACT_APP_API_URL) {
    const envUrl = process.env.REACT_APP_API_URL.trim();
    if (envUrl) return envUrl.replace(/\/$/, "");
  }

  // 2. Vite environment variable (if bundled with Vite)
  try {
    if (typeof import.meta !== "undefined" && import.meta.env && import.meta.env.VITE_API_URL) {
      const viteUrl = import.meta.env.VITE_API_URL.trim();
      if (viteUrl) return viteUrl.replace(/\/$/, "");
    }
  } catch (e) {
    // Ignore if import.meta is not available
  }

  // 3. In browser production (non-localhost)
  if (
    typeof window !== "undefined" &&
    window.location &&
    window.location.hostname &&
    window.location.hostname !== "localhost" &&
    window.location.hostname !== "127.0.0.1"
  ) {
    return "https://backend-pinkmoon.pinkmoontech.com";
  }

  // 4. Default for local development
  return "http://localhost:5000";
};

export const API_BASE_URL = resolveBaseUrl();
const normalisePath = (path) => {
  if (!path) return "";
  return path.startsWith("/") ? path : `/${path}`;
};

export const postForm = async (path, payload) => {
  const url = `${API_BASE_URL}${normalisePath(path)}`;
  const isFormData = payload instanceof FormData;

  let response;
  try {
    const options = {
      method: "POST",
      body: isFormData ? payload : JSON.stringify(payload),
    };

    if (!isFormData) {
      options.headers = {
        "Content-Type": "application/json",
      };
    }

    response = await fetch(url, options);
  } catch (error) {
    console.error("Fetch error:", error);
    throw new Error("Network error or server is unreachable. Please try again later.");
  }

  if (!response) {
    throw new Error("No response from server.");
  }

  let data = null;
  try {
    data = await response.json();
  } catch (error) {
    // Ignore JSON parse errors for empty responses
  }

  if (!response.ok) {
    console.log(response);

    const message = data?.message || "Something went wrong. Please try again.";
    const error = new Error(message);
    error.status = response.status;
    if (data?.missingFields) {
      error.missingFields = data.missingFields;
    }
    error.response = data;
    throw error;
  }

  return data;
};

// --------------- Unified API helpers ----------------

export const submitContact = (payload) =>
  postForm("/api/forms/contact", payload);
export const submitMapContact = (payload) =>
  postForm("/api/forms/map-contact", payload);

export const submitProjectApply = (payload) =>
  postForm("/api/forms/project/apply", payload);
export const submitProjectConversation = (payload) =>
  postForm("/api/forms/project/conversation", payload);
export const submitProjectInvolved = (payload) =>
  postForm("/api/forms/project/involved", payload);

export const submitProductInquiry = (payload) =>
  postForm("/api/forms/product/inquiry", payload);
export const submitProductDemo = (payload) =>
  postForm("/api/forms/product/demo", payload);
export const submitServiceRequest = async (payload) => {
  const url = `${API_BASE_URL}/api/forms/service/request`;
  const isFormData = payload instanceof FormData;

  const options = {
    method: "POST",
  };

  if (isFormData) {
    options.body = payload;
  } else {
    options.headers = {
      "Content-Type": "application/json",
    };
    options.body = JSON.stringify(payload);
  }

  const response = await fetch(url, options);

  let data = null;
  try {
    data = await response.json();
  } catch (e) {
    // ignore
  }

  if (!response.ok) {
    const messageText = data?.message || "Something went wrong. Please try again.";
    const error = new Error(messageText);
    error.status = response.status;
    error.response = data;
    if (data?.missingFields) {
      error.missingFields = data.missingFields;
    }
    throw error;
  }

  return data;
};

export const submitInternship = async (payload) => {
  // Now using the unified endpoint
  const formData = new FormData();
  Object.entries(payload).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      formData.append(key, value);
    }
  });
  // Map specific keys if needed, but the dispatcher handles 'name' as 'fullName' etc.
  // Actually, let's keep it simple: the dispatcher expects req.body.serviceType
  if (!formData.has("serviceType")) formData.append("serviceType", "internship-training");
  if (formData.has("name") && !formData.has("fullName")) formData.append("fullName", formData.get("name"));

  return submitServiceRequest(formData);
};

export const submitProjectGuidance = (payload) => {
  const data = { ...payload, fullName: payload.name, serviceType: "project-guidance" };
  delete data.name;
  return submitServiceRequest(data);
};

export const submitTraining = (payload) => {
  const data = { ...payload, fullName: payload.name, serviceType: "internship-training" };
  delete data.name;
  return submitServiceRequest(data);
};

// Careers - multipart upload (resume)
export const submitCareerApplication = async ({
  name,
  email,
  phone,
  message,
  file,
}) => {
  const url = `${API_BASE_URL}/api/forms/careers`;
  const formData = new FormData();
  formData.append("name", name);
  formData.append("email", email);
  formData.append("phone", phone);
  if (message) formData.append("message", message);
  if (file) formData.append("resume", file);

  const response = await fetch(url, {
    method: "POST",
    body: formData,
  });

  let data = null;
  try {
    data = await response.json();
  } catch (e) {
    // ignore
  }

  if (!response.ok) {
    const messageText =
      data?.message || "Something went wrong. Please try again.";
    const error = new Error(messageText);
    error.status = response.status;
    error.response = data;
    throw error;
  }

  return data;
};

// Legacy compatibility endpoint used by older UIs
export const submitLegacyContact = (payload) =>
  postForm("/api/submit", payload);
